import { z } from 'zod'
import type { CommandEnvelope, CommandError } from './commands.js'
import type { BotLevel, PlayerIndex, PlayerInfo, PlayerView } from './state.js'
import { GameModeSchema, RulesOverrideSchema, type RulesConfig } from './rules.js'
import type { Locale } from './i18n.js'
import type { ChallengeNotice, ChallengeSchema, MatchSummary } from './competition.js'
import type { CreateTournamentSchema, Tournament, TournamentCodeSchema, TournamentNotice, TournamentRef } from './tournament.js'

/** ---- HTTP ---- */

export type GameStatus = 'waiting' | 'active' | 'finished'

export interface GameSummary {
  gameId: string
  status: GameStatus
  players: PlayerInfo[]
  rules: RulesConfig
  /** Invite code, only for the creator of a waiting private game */
  inviteCode?: string
  vsBot: boolean
  /** Present for a tournament game: no rematch, the result feeds the bracket. */
  tournament?: TournamentRef
}

/** ---- Socket.IO ---- */

export const CreateGameSchema = z.object({
  rules: RulesOverrideSchema.optional(),
})

export const JoinGameSchema = z.object({
  inviteCode: z.string().trim().min(4).max(32),
})

export const CreateBotGameSchema = z.object({
  level: z.enum(['easy', 'medium']),
  rules: RulesOverrideSchema.optional(),
  /** Army chosen by the human (random if absent) */
  army: z.enum(['w', 'b']).optional(),
})

export const GameIdSchema = z.object({ gameId: z.string().min(1).max(64) })

/** Ranked queue: players are only matched with players who chose the same mode. */
export const QueueJoinSchema = z.object({ mode: GameModeSchema.default('capture') })

export type Ack<T> = { ok: true; data: T } | { ok: false; error: CommandError }

export interface ClientToServerEvents {
  'game:create': (payload: z.infer<typeof CreateGameSchema>, ack: (r: Ack<GameSummary>) => void) => void
  'game:join': (payload: z.infer<typeof JoinGameSchema>, ack: (r: Ack<GameSummary>) => void) => void
  'game:createVsBot': (
    payload: z.infer<typeof CreateBotGameSchema>,
    ack: (r: Ack<GameSummary>) => void,
  ) => void
  'game:subscribe': (
    payload: z.infer<typeof GameIdSchema>,
    ack: (r: Ack<{ summary: GameSummary; view: PlayerView | null }>) => void,
  ) => void
  'game:resync': (payload: z.infer<typeof GameIdSchema>, ack: (r: Ack<PlayerView>) => void) => void
  'game:command': (payload: CommandEnvelope, ack: (r: Ack<PlayerView>) => void) => void
  'game:rematch': (
    payload: z.infer<typeof GameIdSchema>,
    ack: (r: Ack<{ status: 'proposed' | 'started'; newGameId?: string }>) => void,
  ) => void
  'queue:join': (payload: z.infer<typeof QueueJoinSchema>, ack: (r: Ack<{ queued: boolean; gameId?: string }>) => void) => void
  'queue:leave': (payload: Record<string, never>, ack: (r: Ack<{ left: boolean }>) => void) => void
  /** Challenges a player: creates a private game and notifies them if online. */
  'challenge:send': (payload: z.infer<typeof ChallengeSchema>, ack: (r: Ack<GameSummary>) => void) => void
  /** Tournaments: every answer is the tournament as everybody sees it. */
  'tournament:create': (payload: z.infer<typeof CreateTournamentSchema>, ack: (r: Ack<Tournament>) => void) => void
  'tournament:join': (payload: z.infer<typeof TournamentCodeSchema>, ack: (r: Ack<Tournament>) => void) => void
  /** Reads a tournament and follows its changes (`tournament:update`), without joining it. */
  'tournament:subscribe': (payload: z.infer<typeof TournamentCodeSchema>, ack: (r: Ack<Tournament>) => void) => void
  'tournament:leave': (payload: z.infer<typeof TournamentCodeSchema>, ack: (r: Ack<Tournament>) => void) => void
  'tournament:start': (payload: z.infer<typeof TournamentCodeSchema>, ack: (r: Ack<Tournament>) => void) => void
  'tournament:cancel': (payload: z.infer<typeof TournamentCodeSchema>, ack: (r: Ack<Tournament>) => void) => void
}

export interface ServerToClientEvents {
  /** New filtered state (increasing version). */
  'game:update': (payload: { gameId: string; view: PlayerView }) => void
  /** The game has started or changed status (waiting room -> active). */
  'game:summary': (payload: GameSummary) => void
  'game:presence': (payload: { gameId: string; connected: [boolean, boolean] }) => void
  'game:rematch': (payload: { gameId: string; from: PlayerIndex; newGameId?: string }) => void
  'queue:matched': (payload: { gameId: string }) => void
  'server:error': (payload: CommandError) => void
  /** Ranked result of a finished game (Elo before / after). */
  'game:ranked': (payload: MatchSummary) => void
  'challenge:received': (payload: ChallengeNotice) => void
  /** A followed tournament changed (registration, bracket, result). */
  'tournament:update': (payload: Tournament) => void
  /** One of your tournament matches is ready to be played. */
  'tournament:match': (payload: TournamentNotice) => void
}

export interface SocketAuth {
  token: string
}

export const BOT_LEVEL_LABELS: Record<Locale, Record<BotLevel, string>> = {
  fr: { easy: 'Facile', medium: 'Moyen' },
  en: { easy: 'Easy', medium: 'Medium' },
  es: { easy: 'Fácil', medium: 'Medio' },
  de: { easy: 'Leicht', medium: 'Mittel' },
  hi: { easy: 'आसान', medium: 'मध्यम' },
}

const COMPUTER: Record<Locale, string> = { fr: 'Ordinateur', en: 'Computer', es: 'Ordenador', de: 'Computer', hi: 'कंप्यूटर' }

/** Name of a bot, in the given language ("Ordinateur (Facile)", "Computer (Easy)"). */
export function botName(level: BotLevel, locale: Locale): string {
  return `${COMPUTER[locale]} (${BOT_LEVEL_LABELS[locale][level]})`
}
